import React, { useState } from 'react';
import { ChevronsLeft, ChevronsRight, MoreVertical, User, Sun, Moon, LogOut, X } from 'lucide-react';
import useIsDesktop from '../hooks/useIsDesktop';

function initialsOf(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

function Sidebar({
  navGroups,
  activeKey,
  onNavigate,
  badgeCounts = {},
  collapsed,
  onToggleCollapse,
  userName,
  userRole,
  onLogout,
  theme,
  onToggleTheme,
  mobileOpen,
  onCloseMobile,
}) {
  const isDesktop = useIsDesktop();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const showLabels = isDesktop ? !collapsed : true;
  const ThemeIcon = theme === 'dark' ? Sun : Moon;

  const body = (
    <>
      <div style={styles.top}>
        <a href="/" style={{ ...styles.brand, justifyContent: showLabels ? 'flex-start' : 'center' }}>
          <img src="/logo-mark.png" alt="" style={styles.logo} />
          {showLabels && <span style={styles.wordmark}>Ace Apartments</span>}
        </a>
        {!isDesktop && (
          <button onClick={onCloseMobile} style={styles.closeBtn} aria-label="Close menu">
            <X size={20} />
          </button>
        )}
      </div>

      <nav style={styles.nav} aria-label="Primary">
        {navGroups.map((group) => (
          <div key={group.group} style={styles.group}>
            {showLabels && <p style={styles.groupLabel}>{group.group}</p>}
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = activeKey === item.key;
              const count = badgeCounts[item.key];
              return (
                <button
                  key={item.key}
                  onClick={() => onNavigate(item.key)}
                  className="sidebar-nav-btn"
                  title={!showLabels ? item.label : undefined}
                  style={{
                    ...styles.navBtn,
                    justifyContent: showLabels ? 'flex-start' : 'center',
                    backgroundColor: active ? 'var(--color-primary-light)' : 'transparent',
                    color: active ? 'var(--color-primary)' : 'var(--color-ink-soft)',
                    borderLeft: active ? '3px solid var(--color-primary)' : '3px solid transparent',
                  }}
                >
                  <Icon size={18} />
                  {showLabels && <span style={styles.navLabel}>{item.label}</span>}
                  {showLabels && count > 0 && <span style={styles.pill}>{count}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div style={styles.bottom}>
        {isDesktop && (
          <button onClick={onToggleCollapse} style={styles.collapseBtn} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        )}

        <div style={styles.userWrap}>
          <button
            onClick={() => setUserMenuOpen((o) => !o)}
            style={{ ...styles.userCard, justifyContent: showLabels ? 'flex-start' : 'center' }}
          >
            <div style={styles.avatar}>{initialsOf(userName)}</div>
            {showLabels && (
              <div style={styles.userInfo}>
                <p style={styles.userName}>{userName}</p>
                <span style={styles.roleChip}>{userRole}</span>
              </div>
            )}
            {showLabels && <MoreVertical size={16} color="var(--color-muted)" />}
          </button>

          {userMenuOpen && (
            <div style={styles.userMenu} onMouseLeave={() => setUserMenuOpen(false)}>
              <button style={styles.userMenuItem}>
                <User size={15} /> Profile
              </button>
              <button
                style={styles.userMenuItem}
                onClick={() => {
                  onToggleTheme();
                  setUserMenuOpen(false);
                }}
              >
                <ThemeIcon size={15} /> {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </button>
              <button style={{ ...styles.userMenuItem, color: 'var(--color-danger)' }} onClick={onLogout}>
                <LogOut size={15} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (!isDesktop) {
    if (!mobileOpen) return null;
    return (
      <div style={styles.mobileOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onCloseMobile(); }}>
        <div className="drawer-panel" style={styles.mobileDrawer}>
          {body}
        </div>
      </div>
    );
  }

  return (
    <aside style={{ ...styles.sidebar, width: collapsed ? '72px' : '240px' }}>
      {body}
    </aside>
  );
}

const styles = {
  sidebar: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    backgroundColor: 'var(--color-surface)',
    borderRight: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease',
    zIndex: 50,
  },
  mobileOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(10, 12, 15, 0.55)',
    zIndex: 200,
    display: 'flex',
  },
  mobileDrawer: {
    width: '260px',
    maxWidth: '82vw',
    height: '100%',
    backgroundColor: 'var(--color-surface)',
    borderRight: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
  },
  top: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid var(--color-border)',
    minHeight: '64px',
    boxSizing: 'border-box',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    textDecoration: 'none',
    overflow: 'hidden',
    flex: 1,
  },
  logo: { width: '26px', height: '26px', objectFit: 'contain', flexShrink: 0 },
  wordmark: {
    fontFamily: 'var(--font-display)',
    fontWeight: 800,
    fontSize: '15px',
    color: 'var(--color-ink)',
    whiteSpace: 'nowrap',
  },
  closeBtn: {
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: 'var(--color-ink-soft)',
    cursor: 'pointer',
  },
  nav: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 10px',
  },
  group: {
    marginBottom: '14px',
  },
  groupLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--color-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 6px',
    padding: '0 10px',
  },
  navBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '9px 10px',
    marginBottom: '2px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13.5px',
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
  },
  navLabel: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pill: {
    backgroundColor: 'var(--color-warning)',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    borderRadius: '999px',
    padding: '1px 7px',
    flexShrink: 0,
  },
  bottom: {
    borderTop: '1px solid var(--color-border)',
    padding: '10px',
  },
  collapseBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    marginBottom: '8px',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    background: 'transparent',
    color: 'var(--color-ink-soft)',
    cursor: 'pointer',
  },
  userWrap: {
    position: 'relative',
  },
  userCard: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px',
    border: 'none',
    borderRadius: '10px',
    background: 'transparent',
    cursor: 'pointer',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-primary-light)',
    color: 'var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 800,
    flexShrink: 0,
  },
  userInfo: {
    flex: 1,
    overflow: 'hidden',
    textAlign: 'left',
  },
  userName: {
    fontSize: '13px',
    fontWeight: 700,
    color: 'var(--color-ink)',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  roleChip: {
    fontSize: '11px',
    color: 'var(--color-muted)',
    textTransform: 'capitalize',
  },
  userMenu: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    right: 0,
    marginBottom: '6px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '10px',
    boxShadow: 'var(--shadow-md)',
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
  },
  userMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 10px',
    fontSize: '13px',
    fontWeight: 600,
    border: 'none',
    background: 'transparent',
    borderRadius: '7px',
    cursor: 'pointer',
    color: 'var(--color-ink)',
    textAlign: 'left',
  },
};

export default Sidebar;
